<template>
  <div v-if="alerts.length" class="expiry-carousel">
    <el-carousel
      height="118px"
      :interval="4000"
      arrow="never"
      indicator-position="none"
    >
      <el-carousel-item v-for="(page, pi) in pages" :key="pi">
        <div class="page-row">
          <div
            v-for="item in page"
            :key="item.key"
            class="alert-card"
            :class="item.level"
            :title="`点击前往${item.moduleLabel}`"
            @click="go(item)"
          >
            <div class="card-top">
              <span class="module-tag">{{ item.moduleLabel }}</span>
              <span class="days">{{ item.daysText }}</span>
            </div>
            <div class="card-mid">
              <span class="dept">{{ item.department || '-' }}</span>
              <span class="sep">·</span>
              <span class="device">{{ item.device_name || '-' }}</span>
            </div>
            <div class="card-bot">有效期至 {{ item.expire_date || '-' }}</div>
          </div>
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>

<script>
import measurementApi from '../api/measurementReminder'
import siteApi from '../api/sitePerformance'
import valveApi from '../api/safetyValve'

// 待报检阈值（天）：与三个列表页保持同一口径 —— <=30 天为待报检，<0 为已超期
const PENDING_DAYS = 30
// 每页展示条数：1 = 单卡轮播，只占「原来第一张卡」的位置，
// 右侧留白给后续主页要加的效益分析等设备数据看板
const PAGE_SIZE = 1

const MODULES = [
  {
    key: 'measurement',
    label: '计量提醒',
    path: '/report-reminder/measurement',
    api: measurementApi,
  },
  {
    key: 'site',
    label: '场所性能',
    path: '/report-reminder/site-performance',
    api: siteApi,
  },
  {
    key: 'valve',
    label: '安全阀提醒',
    path: '/report-reminder/safety-valve',
    api: valveApi,
  },
]

export default {
  name: 'ExpiryCarousel',
  data() {
    return {
      alerts: [],
    }
  },
  computed: {
    pages() {
      const pages = []
      for (let i = 0; i < this.alerts.length; i += PAGE_SIZE) {
        pages.push(this.alerts.slice(i, i + PAGE_SIZE))
      }
      return pages
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    // 今天 + n 天，返回 yyyy-MM-dd（用于 expire_date_end 上界）
    addDays(n) {
      const d = new Date()
      d.setDate(d.getDate() + n)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async fetch() {
      const endDate = this.addDays(PENDING_DAYS)
      const settled = await Promise.all(
        MODULES.map(m =>
          m.api
            .list({ expire_date_end: endDate, page_size: 100 })
            .then(res => ({ mod: m, data: res.data }))
            .catch(() => null) // 单模块失败不影响其他模块
        )
      )

      const alerts = []
      settled.forEach(item => {
        if (!item) return
        const list = item.data.results || item.data
        if (!Array.isArray(list)) return
        list.forEach(row => {
          const d = row.days_until_expire
          // 无有效期（days_until_expire 为 null）的记录不参与提醒
          if (d === null || d === undefined) return
          alerts.push({
            key: `${item.mod.key}-${row.id}`,
            moduleLabel: item.mod.label,
            path: item.mod.path,
            department: row.department,
            device_name: row.device_name,
            expire_date: row.expire_date,
            days: d,
            level: d < 0 ? 'overdue' : 'pending',
            daysText: d < 0 ? `已超期 ${Math.abs(d)} 天` : `剩余 ${d} 天`,
          })
        })
      })

      // 最紧急的排前面：已超期优先，其次剩余天数少的
      alerts.sort((a, b) => a.days - b.days)
      this.alerts = alerts
    },
    go(item) {
      this.$router.push(item.path)
    },
  },
}
</script>

<style scoped>
.expiry-carousel {
  margin: 16px;
  /* 只占原第一张卡的宽度：(容器 100% − 左右 margin 32px − 3 卡之间 gap 24px) / 3 */
  width: calc((100% - 56px) / 3);
  min-width: 320px;
  box-sizing: border-box;
}
.page-row {
  display: flex;
  gap: 12px;
  height: 100%;
}
.alert-card {
  flex: 1;
  min-width: 0;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  border-left: 4px solid;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: box-shadow 0.2s, transform 0.2s;
}
.alert-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}
/* 已超期：深红色背景 */
.alert-card.overdue {
  background: linear-gradient(135deg, #f8c9c9 0%, #f3a9a9 100%);
  border-left-color: #d32f2f;
  color: #9a1b1b;
}
/* 待报检：深黄色背景 */
.alert-card.pending {
  background: linear-gradient(135deg, #f9e0a8 0%, #f5d08a 100%);
  border-left-color: #d48806;
  color: #8a5e0a;
}
.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.module-tag {
  font-size: 13px;
  font-weight: bold;
  padding: 1px 8px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.75);
}
.days {
  font-size: 16px;
  font-weight: bold;
}
.card-mid {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-mid .sep {
  margin: 0 6px;
  opacity: 0.6;
}
.card-mid .dept {
  opacity: 0.85;
}
.card-bot {
  font-size: 13px;
  opacity: 0.8;
}
</style>
